import { useEffect, useState } from "react";
import { apiClient } from "../api/apiClient.js";

/**
 * P157-R10: the row avatar. Shows the worker's real uploaded photo when there is
 * one, and their initials when there is not.
 *
 * Why it probes rather than reading a flag: neither list endpoint reports
 * whether a photo exists (the e-KYC queue and the ready-for-match candidate DTOs
 * predate the photo columns), but both rows DO carry the application id the
 * photo hangs off - the queue row is keyed by it, and a candidate carries
 * sourceApplicationId. So the path is derivable today without a backend change,
 * and a 404 is simply "no photo", which is the common case and a perfectly
 * honest answer.
 *
 * The cache below is what keeps that from being wasteful: one request per
 * application for the life of the page, and a negative result is remembered too,
 * so paging back and forth never re-asks for a photo that is not there. A real
 * `hasPhoto` flag on the two list DTOs would remove the probe entirely and is
 * the right follow-up if these grids ever get long enough for it to show.
 */
const cache = new Map();

function photoPath(applicationId) {
  return `/backoffice/worker-applications/${encodeURIComponent(applicationId)}/detailed-profile/photo/file`;
}

export default function WorkerRowAvatar({ applicationId, initials }) {
  const [objectUrl, setObjectUrl] = useState(() =>
    applicationId && cache.get(applicationId)?.status === "ready" ? cache.get(applicationId).objectUrl : null,
  );

  useEffect(() => {
    if (!applicationId) {
      setObjectUrl(null);
      return undefined;
    }

    const cached = cache.get(applicationId);
    if (cached?.status === "ready") {
      setObjectUrl(cached.objectUrl);
      return undefined;
    }
    if (cached?.status === "none") {
      setObjectUrl(null);
      return undefined;
    }

    let cancelled = false;
    cache.set(applicationId, { status: "loading" });
    apiClient
      .getBlob(photoPath(applicationId))
      .then(({ blob, contentType }) => {
        // A response can be 2xx and still not be a picture (an error envelope,
        // an empty body). Rendering that produces the browser's broken-image
        // glyph, which looks like a bug in the grid rather than the honest
        // "this worker has no photo" the initials say.
        const type = blob?.type || contentType || "";
        if (!type.startsWith("image/") || !blob || blob.size === 0) {
          throw new Error("not an image");
        }
        return blob;
      })
      .then((blob) => {
        // Deliberately NOT revoked on unmount: the URL is shared through the
        // cache with every other row/render for this worker, so revoking on the
        // first unmount would blank the picture everywhere else.
        const url = URL.createObjectURL(blob);
        cache.set(applicationId, { status: "ready", objectUrl: url });
        if (!cancelled) {
          setObjectUrl(url);
        }
      })
      .catch(() => {
        cache.set(applicationId, { status: "none" });
        if (!cancelled) {
          setObjectUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  if (objectUrl) {
    return (
      <span className="wx-aw-avatar wx-aw-avatar--photo">
        {/* Last line of defence: if the bytes still fail to decode, drop back to
            initials rather than leaving a broken-image glyph in the row. */}
        <img
          src={objectUrl}
          alt=""
          onError={() => {
            cache.set(applicationId, { status: "none" });
            setObjectUrl(null);
          }}
        />
      </span>
    );
  }

  return (
    <span className="wx-aw-avatar" aria-hidden="true">
      {initials}
    </span>
  );
}
